import Loadable from "@/components/building-blocks/controls/Loadable"
import AuthGuard from "@/components/building-blocks/routes/AuthGuard"
import { OptionalRouterParam, RequiredRouterParam } from "@/components/building-blocks/routes/RouteParams"
import { AppAdminRoutes } from "@/config/routes"
import { lazy } from "react"
import { RouteObject } from "react-router-dom"

const VersionPage = Loadable(lazy(() => import("@/pages/system/VersionPage")))
const SystemStatusPage = Loadable(lazy(() => import("@/pages/system/StatusPage")))

const DashboardOverviewPage = Loadable(lazy(() => import("@/pages/dashboard/DashboardOverviewPage")))
const AdminLayout = Loadable(lazy(() => import("@/layout/AdminLayout")))
const CompaniesOverviewPage = Loadable(lazy(() => import("@/pages/company/CompaniesOverviewPage")))
const PaymentRequestsOverviewPage = Loadable(lazy(() => import("@/pages/payment-request/PaymentRequestsOverviewPage")))
const SuppliersOverviewPage = Loadable(lazy(() => import("@/pages/supplier/SuppliersOverviewPage")))
const UsersOverviewPage = Loadable(lazy(() => import("@/pages/user/UsersOverviewPage")))
const InvoicesOverviewPage = Loadable(lazy(() => import("@/pages/invoice/InvoicesOverviewPage")))
const InvoicesExportPage = Loadable(lazy(() => import("@/pages/invoice/InvoicesExportPage")))
const InvoicePreview = Loadable(lazy(() => import("@/pages/invoice/InvoiceAttachmentPreviewPage")))

const UserForm = Loadable(lazy(() => import("@/components/user/Form")))
const SupplierForm = Loadable(lazy(() => import("@/components/supplier/Form")))
const PaymentRequestForm = Loadable(lazy(() => import("@/components/payment-request/Form")))
const CompanyForm = Loadable(lazy(() => import("@/components/company/Form")))
const NewInvoice = Loadable(lazy(() => import("@/components/invoice/new")))
const InvoiceDetail = Loadable(lazy(() => import("@/components/invoice/detail")))

const ArchiveOverviewPage = Loadable(lazy(() => import("@/pages/archive/ArchiveOverviewPage")))
const ArchiveUpload = Loadable(lazy(() => import("@/components/archive/Upload")))
// ==============================|| MAIN ROUTES ||============================== //

const AdminRoutes: RouteObject = {
    path: "/",
    element: (
        <AuthGuard>
            <AdminLayout />
        </AuthGuard>
    ),
    children: [
        {
            path: AppAdminRoutes.system.version.path,
            element: (
                <RequiredRouterParam paramId="version">
                    {(param) => <VersionPage version={param} />}
                </RequiredRouterParam>
            ),
        },
        {
            path: AppAdminRoutes.system.status.path,
            element: <SystemStatusPage />,
        },
        {
            path: AppAdminRoutes.dashboard.path,
            element: <DashboardOverviewPage />,
        },
        {
            path: AppAdminRoutes.archive.path,
            element: <ArchiveOverviewPage />,
            children: [
                {
                    path: AppAdminRoutes.archive.children.upload.path,
                    element: <ArchiveUpload />,
                },
            ],
        },
        {
            path: AppAdminRoutes.companies.path,
            element: (
                <OptionalRouterParam paramId="companyId">
                    {(param) => <CompaniesOverviewPage selectedId={param ? Number(param) : undefined} />}
                </OptionalRouterParam>
            ),
            children: [
                {
                    path: AppAdminRoutes.companies.children.new.path,
                    element: <CompanyForm />,
                },
                {
                    path: AppAdminRoutes.companies.children.edit.path,
                    element: (
                        <RequiredRouterParam paramId="companyId">
                            {(param) => <CompanyForm id={Number(param)} />}
                        </RequiredRouterParam>
                    ),
                },
            ],
        },
        {
            path: AppAdminRoutes.suppliers.path,
            element: (
                <OptionalRouterParam paramId="supplierId">
                    {(param) => <SuppliersOverviewPage selectedId={param ? Number(param) : undefined} />}
                </OptionalRouterParam>
            ),
            children: [
                {
                    path: AppAdminRoutes.suppliers.children.new.path,
                    element: <SupplierForm />,
                },
                {
                    path: AppAdminRoutes.suppliers.children.edit.path,
                    element: (
                        <RequiredRouterParam paramId="supplierId">
                            {(param) => <SupplierForm id={Number(param)} />}
                        </RequiredRouterParam>
                    ),
                },
            ],
        },
        {
            path: AppAdminRoutes.users.path,
            element: (
                <OptionalRouterParam paramId="userId">
                    {(param) => <UsersOverviewPage selectedId={param ? Number(param) : undefined} />}
                </OptionalRouterParam>
            ),
            children: [
                {
                    path: AppAdminRoutes.users.children.new.path,
                    element: <UserForm />,
                },
                {
                    path: AppAdminRoutes.users.children.edit.path,
                    element: (
                        <RequiredRouterParam paramId="userId">
                            {(param) => <UserForm id={Number(param)} />}
                        </RequiredRouterParam>
                    ),
                },
            ],
        },
        {
            path: AppAdminRoutes.paymentRequest.path,
            element: (
                <OptionalRouterParam paramId="requestId">
                    {(param) => <PaymentRequestsOverviewPage selectedId={param ? Number(param) : undefined} />}
                </OptionalRouterParam>
            ),
            children: [
                {
                    path: AppAdminRoutes.paymentRequest.children.new.path,
                    element: <PaymentRequestForm />,
                },
                {
                    path: AppAdminRoutes.paymentRequest.children.edit.path,
                    element: (
                        <RequiredRouterParam paramId="requestId">
                            {(param) => <PaymentRequestForm id={Number(param)} />}
                        </RequiredRouterParam>
                    ),
                },
            ],
        },
        {
            path: AppAdminRoutes.invoices.path,
            element: (
                <OptionalRouterParam paramId="invoiceId">
                    {(param) => <InvoicesOverviewPage selectedId={param ? Number(param) : undefined} />}
                </OptionalRouterParam>
            ),
            children: [
                {
                    path: AppAdminRoutes.invoices.children.new.path,
                    element: <NewInvoice />,
                },
                {
                    path: AppAdminRoutes.invoices.children.detail.path,
                    element: (
                        <RequiredRouterParam paramId="invoiceId">
                            {(param) => <InvoiceDetail id={Number(param)} />}
                        </RequiredRouterParam>
                    ),
                },
            ],
        },
        {
            path: AppAdminRoutes.invoicePreview.path,
            element: (
                <RequiredRouterParam paramId="invoiceId">
                    {(param) => <InvoicePreview invoiceId={Number(param)} />}
                </RequiredRouterParam>
            ),
        },
        {
            path: AppAdminRoutes.utils.invoicesExport.path,
            element: <InvoicesExportPage />,
        },
    ],
}

export default AdminRoutes
